import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ScrollReveal } from "@/components/ScrollReveal";
import { useLocale } from "@/i18n/LocaleProvider";
import { scrollToSection } from "@/lib/scrollToSection";
import { MessagesSquare, ReceiptText, UserRound, ArrowRight, Sparkles } from "lucide-react";

const icons = [MessagesSquare, ReceiptText, UserRound];

const PlainPathSection = () => {
  const { t } = useTranslation();
  const { localize } = useLocale();
  const items = t("plainPath.items", { returnObjects: true }) as {
    title: string;
    desc: string;
  }[];

  return (
    <section id="plain-path" className="relative section-y-wide bg-background overflow-hidden border-t border-border">
      <div className="absolute inset-0 tech-grid opacity-[0.08] pointer-events-none" />
      <div
        className="absolute -top-1/3 right-1/4 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(186 100% 44% / 0.07) 0%, transparent 65%)" }}
      />

      <div className="container relative z-10">
        <ScrollReveal className="max-w-2xl">
          <h2 className="text-[1.55rem] sm:text-3xl md:text-5xl font-bold text-foreground leading-[1.1] tracking-tight">
            {t("plainPath.title")}
          </h2>
          <p className="mt-6 text-muted-foreground text-[15px] sm:text-lg md:text-xl leading-relaxed">
            {t("plainPath.subtitle")}
          </p>
        </ScrollReveal>

        <div className="mt-12 md:mt-16 grid gap-5 md:gap-6 md:grid-cols-3">
          {items.map((item, i) => {
            const Icon = icons[i] ?? MessagesSquare;
            return (
              <ScrollReveal key={item.title} delay={i * 100}>
                <div className="group h-full rounded-2xl border border-border bg-card/50 backdrop-blur-xl p-5 md:p-8 transition-colors duration-300 hover:border-accent/40">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-accent/10 border border-accent/25 text-accent transition-[background-color,box-shadow] duration-200 group-hover:bg-accent/20 group-hover:shadow-[0_0_16px_hsl(var(--accent)/0.3)]">
                      <Icon size={20} strokeWidth={2} />
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="mt-6 text-xl font-semibold text-foreground">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>
            );
          })}
        </div>

        <ScrollReveal delay={150} className="mt-12 md:mt-14 flex flex-col sm:flex-row gap-3 md:gap-4">
          <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
            <Link
              to={localize("/contact")}
              className="group inline-flex w-full sm:w-auto justify-center items-center gap-2 px-7 py-4 btn-clip bg-primary text-primary-foreground btn-label-contrast font-medium transition-transform duration-200 active:scale-[0.97]"
            >
              {t("plainPath.cta")}
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </span>
          <span className="hover-glow-accent block w-full sm:inline-block sm:w-auto">
            <a
              href="#blueprint"
              onClick={(e) => { e.preventDefault(); scrollToSection("blueprint"); }}
              className="inline-flex w-full sm:w-auto justify-center items-center gap-2 px-7 py-4 btn-clip bg-card/40 text-foreground font-medium transition-transform duration-200 active:scale-[0.97]"
            >
              <Sparkles size={16} className="text-accent" />
              {t("common.freeBlueprint")}
            </a>
          </span>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default PlainPathSection;
